// src/components/message-board/MessagePost.js
import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Edit2, Trash2, Pin, PinOff, MoreVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import MessageComposer from './MessageComposer';

const MessagePost = ({ 
  message, 
  leagueId,
  currentUserId,
  isCommissioner,
  onReply,
  onEdit,
  onDelete,
  onTogglePin,
  showReplies = true
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const menuRef = useRef(null);

  const isOwnMessage = message.user_id === currentUserId;
  const canDelete = isOwnMessage || isCommissioner;
  const canPin = isCommissioner && !message.parent_message_id;

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return created.toLocaleDateString();
  };

  const handleReply = async (replyData) => {
    await onReply(message.message_id, replyData);
    setShowReplyForm(false);
  };

  const handleEdit = async (editData) => {
    await onEdit(message.message_id, editData);
    setShowEditForm(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      await onDelete(message.message_id);
    }
  };

  const handleTogglePin = async () => {
    try {
      // Explicitly convert to boolean and send the opposite of current state
      const newPinState = !Boolean(message.is_pinned);
      await onTogglePin(message.message_id, newPinState);
    } catch (err) {
      console.error('Error toggling pin:', err);
      alert(err?.response?.data?.detail || 'Failed to toggle pin. Please try again.');
    }
  };

  // YouTube URL detection and parsing (FIXED REGEX)
  const extractYouTubeId = (url) => {
    // Match various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,  // watch?v= or youtu.be/
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,                      // embed/
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,                          // v/
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/                      // shorts/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // GIF URL detection (GIPHY, Tenor)
  const isGifUrl = (url) => {
    // Check for GIPHY or Tenor URLs
    if (url.match(/giphy\.com\/gifs/i) || url.match(/giphy\.com\/media/i)) {
      return { type: 'giphy', url };
    }
    if (url.match(/tenor\.com\/view/i) || url.match(/tenor\.com\/.*\.gif/i)) {
      return { type: 'tenor', url };
    }
    // Check for direct GIF URLs
    if (url.match(/\.(gif)(\?|$)/i)) {
      return { type: 'direct', url };
    }
    return null;
  };

  const parseMessageWithMedia = (text) => {
    if (!text) return [];  // Return empty array instead of null
    
    // Regex to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0];
      const videoId = extractYouTubeId(url);
      const gifInfo = isGifUrl(url);
      
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add YouTube embed, GIF embed, or regular link
      if (videoId) {
        parts.push({
          type: 'youtube',
          videoId: videoId,
          url: url
        });
      } else if (gifInfo) {
        parts.push({
          type: 'gif',
          gifType: gifInfo.type,
          url: url
        });
      } else {
        parts.push({
          type: 'link',
          url: url
        });
      }
      
      lastIndex = match.index + url.length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Team Logo Component
  const TeamLogo = () => {
    if (message.team_logo_url) {
      return (
        <img 
          src={message.team_logo_url}
          alt={`${message.team_name} Logo`}
          className="w-10 h-10 rounded-full object-cover border-2 border-neutral-700"
          onError={(e) => {
            e.target.style.display = 'none';
            if (e.target.nextElementSibling) {
              e.target.nextElementSibling.style.display = 'flex';
            }
          }}
        />
      );
    }

    const teamColors = message.team_colors || {};
    return (
      <div 
        className="w-10 h-10 rounded-full border-2 border-neutral-700 flex items-center justify-center text-xs font-bold"
        style={{
          background: teamColors.primary ? 
            `linear-gradient(135deg, ${teamColors.primary}, ${teamColors.secondary || teamColors.primary})` : 
            'linear-gradient(135deg, #FFD700, #8B7500)',
          color: teamColors.text || '#000000'
        }}
      >
        {message.team_name?.substring(0, 2).toUpperCase() || 'TM'}
      </div>
    );
  };

  return (
    <div className={`${message.is_pinned ? 'border-l-4 border-yellow-400' : ''}`}>
      <div className={`${dynastyTheme.components.card.base} ${
        message.is_pinned ? 'bg-yellow-400/5' : ''
      } p-4 ${dynastyTheme.classes.transition}`}>
        {/* Message Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1">
            <TeamLogo />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-semibold ${dynastyTheme.classes.text.white}`}>
                  {message.owner_name || 'Unknown'}
                </span>
                <span className={`text-sm ${dynastyTheme.classes.text.primary}`}>
                  {message.team_name}
                </span>
                {message.is_pinned && (
                  <span className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} font-semibold`}>
                    PINNED
                  </span>
                )}
                {message.is_edited && (
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} italic`}>
                    (edited)
                  </span>
                )}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                {formatTimeAgo(message.created_at)}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className={`p-1 rounded ${dynastyTheme.classes.text.neutralLight} hover:bg-neutral-800 ${dynastyTheme.classes.transition}`}
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl z-[9999]">
                <div className="py-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowReplyForm(true);
                      setShowMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm ${dynastyTheme.classes.text.white} hover:bg-neutral-800 ${dynastyTheme.classes.transition} flex items-center gap-2`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Reply
                  </button>
                  
                  {isOwnMessage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditForm(true);
                        setShowMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${dynastyTheme.classes.text.white} hover:bg-neutral-800 ${dynastyTheme.classes.transition} flex items-center gap-2`}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  )}

                  {canPin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePin();
                        setShowMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${dynastyTheme.classes.text.warning} hover:bg-neutral-800 ${dynastyTheme.classes.transition} flex items-center gap-2`}
                    >
                      {message.is_pinned ? (
                        <>
                          <PinOff className="w-4 h-4" />
                          Unpin
                        </>
                      ) : (
                        <>
                          <Pin className="w-4 h-4" />
                          Pin
                        </>
                      )}
                    </button>
                  )}

                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                        setShowMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${dynastyTheme.classes.text.error} hover:bg-neutral-800 ${dynastyTheme.classes.transition} flex items-center gap-2`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Message Content */}
        {showEditForm ? (
          <MessageComposer
            onSubmit={handleEdit}
            onCancel={() => setShowEditForm(false)}
            placeholder="Edit your message..."
            buttonText="Save Changes"
            autoFocus
          />
        ) : (
          <div className="mb-3">
            {parseMessageWithMedia(message.message_text).map((part, idx) => {
              if (part.type === 'text') {
                return (
                  <span key={idx} className={`${dynastyTheme.classes.text.white} whitespace-pre-wrap break-words`}>
                    {part.content}
                  </span>
                );
              } else if (part.type === 'youtube') {
                return (
                  <div key={idx} className="my-3">
                    <div className="aspect-video w-full max-w-2xl rounded-lg overflow-hidden border border-neutral-700">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${part.videoId}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <a 
                      href={part.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`text-xs ${dynastyTheme.classes.text.primary} hover:underline mt-1 inline-block`}
                    >
                      {part.url}
                    </a>
                  </div>
                );
              } else if (part.type === 'gif') {
                return (
                  <div key={idx} className="my-3">
                    <div className="max-w-md rounded-lg overflow-hidden border border-neutral-700">
                      <img
                        src={part.url}
                        alt="GIF"
                        className="w-full h-auto"
                        loading="lazy"
                      />
                    </div>
                    <a 
                      href={part.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`text-xs ${dynastyTheme.classes.text.primary} hover:underline mt-1 inline-block`}
                    >
                      {part.url}
                    </a>
                  </div>
                );
              } else if (part.type === 'link') {
                return (
                  <a 
                    key={idx}
                    href={part.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`${dynastyTheme.classes.text.primary} hover:underline break-all`}
                  >
                    {part.url}
                  </a>
                );
              }
              return null;
            })}
          </div>
        )}

        {/* Images (if any) */}
        {message.message_data?.images && message.message_data.images.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {message.message_data.images.map((img, idx) => (
              <img 
                key={idx}
                src={img.url}
                alt={`Attachment ${idx + 1}`}
                className="rounded border border-neutral-700 w-full object-contain"
              />
            ))}
          </div>
        )}

        {/* Action Buttons */}
        {!showEditForm && !message.parent_message_id && (
          <div className="flex items-center gap-3 pt-2 border-t border-neutral-700/30">
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className={`flex items-center gap-1 text-sm ${dynastyTheme.classes.text.neutralLight} hover:text-yellow-400 ${dynastyTheme.classes.transition}`}
            >
              <MessageSquare className="w-4 h-4" />
              Reply {message.reply_count > 0 && `(${message.reply_count})`}
            </button>
            
            {/* Collapse/Expand button - only show if there are replies */}
            {message.reply_count > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`flex items-center gap-1 text-sm ${dynastyTheme.classes.text.neutralLight} hover:text-yellow-400 ${dynastyTheme.classes.transition}`}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Collapse
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Expand {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Reply Form */}
        {showReplyForm && (
          <div className="mt-3 pl-4 md:pl-8">
            <MessageComposer
              leagueId={leagueId}
              onSubmit={handleReply}
              onCancel={() => setShowReplyForm(false)}
              placeholder="Write a reply..."
              isReply
              autoFocus
              buttonText="Post Reply"
              allowImageUpload={true}
            />
          </div>
        )}

        {/* Replies - only show if expanded */}
        {showReplies && isExpanded && message.replies && message.replies.length > 0 && (
          <div className="mt-3 pl-4 md:pl-8 space-y-3 border-l-2 border-neutral-700">
            {message.replies.map((reply) => (
              <MessagePost
                key={reply.message_id}
                message={reply}
                leagueId={leagueId}
                currentUserId={currentUserId}
                isCommissioner={isCommissioner}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                showReplies={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagePost;
